/**
 * Ticket Lifecycle v2 — Slice 4E
 *
 * Small, additive "Advisor Activity" card that surfaces the first
 * reliable Service Advisor Passport metrics derived from existing
 * custody + advisor-action data:
 *
 *   - Tickets accepted (last 30 days)
 *   - Tickets kicked back to tech (last 30 days)
 *   - Accept lag (mean, p50, p95)
 *   - Avg FOH custody duration
 *   - Live pending-advisor-accept / with-advisor counts (shop-wide)
 *
 * Flag-gated by `advisorTimingAnalytics`. Mounts inside
 * `AdvisorPassport.tsx` below the existing sections. Renders nothing
 * when the flag is off.
 *
 * "Insufficient data" rendering rule:
 *   - Counts render as `0` (valid).
 *   - Mean/p50/p95 render as `—` when null (empty sample or sub-3
 *     for percentiles). The pure helper produces the null values;
 *     this component only renders.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useAdvisorMetrics } from '@/hooks/tickets/useAdvisorMetrics';
import { ADVISOR_METRICS_WINDOW_DAYS } from '@/lib/tickets/lifecycle/advisorMetrics';

interface Props {
  advisorUserId: string;
}

function formatMinutes(value: number | null): string {
  if (value == null) return '—';
  if (value < 1) return '< 1m';
  if (value < 60) return `${Math.round(value)}m`;
  const hours = Math.floor(value / 60);
  const minutes = Math.round(value % 60);
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function AdvisorActivityCard({ advisorUserId }: Props) {
  const { activeShop } = useAuth();
  const advisorTimingAnalyticsEnabled = useFeatureFlag('advisorTimingAnalytics');
  const { data, isLoading, isError, error } = useAdvisorMetrics({
    shopId: activeShop?.id ?? null,
    advisorUserId,
  });

  // Flag-off → render nothing. Hook is internally gated too; this
  // second gate avoids any layout shift in the parent AdvisorPassport.
  if (!advisorTimingAnalyticsEnabled) return null;

  if (isLoading) {
    return (
      <Card data-testid="advisor-activity-card">
        <CardHeader>
          <CardTitle>Advisor Activity</CardTitle>
          <CardDescription>Last {ADVISOR_METRICS_WINDOW_DAYS} days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading metrics…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="advisor-activity-card">
        <CardHeader>
          <CardTitle>Advisor Activity</CardTitle>
          <CardDescription className="text-destructive">
            Failed to load advisor metrics. The slice-4B advisor_action_events
            migration may not be applied to this Supabase project.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            {(error as Error)?.message || 'Unknown error.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const noActivity =
    data.ticketsAcceptedCount === 0 &&
    data.ticketsKickedBackToTechCount === 0 &&
    data.sampleSize.acceptLagPairs === 0 &&
    data.sampleSize.fohCustodyPairs === 0 &&
    data.sampleSize.advisorLifecycleCycles === 0 &&
    data.currentPendingAdvisorAcceptCount === 0 &&
    data.currentWithAdvisorCount === 0;

  return (
    <Card data-testid="advisor-activity-card">
      <CardHeader>
        <CardTitle>Advisor Activity</CardTitle>
        <CardDescription>
          Last {ADVISOR_METRICS_WINDOW_DAYS} days
        </CardDescription>
      </CardHeader>
      <CardContent>
        {noActivity ? (
          <p className="text-sm text-muted-foreground">
            Insufficient data — no advisor activity in the last {ADVISOR_METRICS_WINDOW_DAYS} days.
          </p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat
                label="Tickets accepted"
                value={String(data.ticketsAcceptedCount)}
              />
              <Stat
                label="Kicked back to tech"
                value={String(data.ticketsKickedBackToTechCount)}
              />
              <Stat
                label="Pending accept"
                value={String(data.currentPendingAdvisorAcceptCount)}
                hint="Shop-wide right now"
              />
              <Stat
                label="With advisor"
                value={String(data.currentWithAdvisorCount)}
                hint="Shop-wide right now"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t">
              <Stat
                label="Average accept time"
                value={formatMinutes(data.averageAcceptLagMinutes)}
                hint={`Based on ${data.sampleSize.acceptLagPairs} handoff${data.sampleSize.acceptLagPairs === 1 ? '' : 's'}`}
              />
              <Stat
                label="Typical accept time"
                value={formatMinutes(data.p50AcceptLagMinutes)}
              />
              <Stat
                label="Slowest normal accept time"
                value={formatMinutes(data.p95AcceptLagMinutes)}
              />
              <Stat
                label="Average advisor time"
                value={formatMinutes(data.averageFohCustodyMinutes)}
                hint={`Based on ${data.sampleSize.fohCustodyPairs} advisor cycle${data.sampleSize.fohCustodyPairs === 1 ? '' : 's'}`}
              />
              <Stat
                label="Average advisor lifecycle time"
                value={formatMinutes(data.averageAdvisorLifecycleMinutes)}
                hint={`Tech kick → tech back · ${data.sampleSize.advisorLifecycleCycles} full cycle${data.sampleSize.advisorLifecycleCycles === 1 ? '' : 's'}`}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
